import React, { useEffect, useState } from "react";
import { FilteredItems } from "./components/FilteredItems/FilteredItems";
import { ChatAI } from "./components/ChatAI/ChatAI";
import { IListItem } from "../../interfaces/IDocDetails";
import { useTranslation } from "react-i18next";

interface IChatBlockProps {
  listItem: IListItem[];
  offset: number;
}

interface ISelectedDocument {
  code: string;
  case_no: string;
}

const ChatBlock: React.FC<IChatBlockProps> = ({ listItem, offset }) => {
  const { t } = useTranslation();

  // true = chat with most relevant documents, so no explicit ids are sent
  const [isMostRelevantSelected, setIsMostRelevantSelected] = useState(true);
  const [selectedDocuments, setSelectedDocuments] = useState<ISelectedDocument[]>([]);

  useEffect(() => {
    // When list changes, keep default mode active and clear explicit ids.
    setIsMostRelevantSelected(true);
    setSelectedDocuments([]);
  }, [listItem]);

  const handleMostRelevantDocuments = () => {
    setIsMostRelevantSelected(true);
    setSelectedDocuments([]);
  };

  const handleSelectDocuments = (code: string, case_no: string) => {
    setIsMostRelevantSelected(false);

    setSelectedDocuments((prevSelected) => {
      const exists = prevSelected.find(
        (doc: ISelectedDocument) =>
          doc.code === code && doc.case_no === case_no,
      );

      if (exists) {
        return prevSelected.filter(
          (doc: ISelectedDocument) =>
            !(doc.code === code && doc.case_no === case_no),
        );
      }

      return [...prevSelected, { code, case_no }];
    });
  };

  const chatSelectedDocuments = isMostRelevantSelected ? [] : selectedDocuments;

  return (
    <>
      <div className="w-1/4 flex flex-col overflow-y-auto">
        <div className="flex items-center justify-between px-2">
          <label className="flex items-center title-base-color space-x-2">
            <input
              type="checkbox"
              checked={isMostRelevantSelected}
              onChange={handleMostRelevantDocuments}
              className="ml-1 accent-del-light-theme-green dark:accent-del-green"
            />
            <span className="ml-2 text-sm">
              {t("chatAI.selectAll")}
            </span>
          </label>
        </div>

        {listItem.length > 0 &&
          listItem.map((item: IListItem) => {
            const itemKey = `${item.code}-${item.case_no ?? item.id}`;
            const isSelected =
              !isMostRelevantSelected &&
              !!selectedDocuments.find(
                (doc) =>
                  doc.code === item.code && doc.case_no === item.case_no,
              );

            return (
              <div
                key={`${itemKey}-${offset}`}
                className="text-gray-800 dark:text-gray-300 p-1"
              >
                <FilteredItems
                  data={item}
                  isSelected={isSelected}
                  onSelect={handleSelectDocuments}
                />
              </div>
            );
          })}
      </div>

      <div className="w-3/4 flex flex-col flex-1 min-h-0">
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden flex-1 min-h-0">
          <ChatAI
            selectedDocuments={chatSelectedDocuments}
            setSelectedDocuments={setSelectedDocuments}
          />
        </div>

        <div className="mt-2 text-xs text-slate-500 dark:text-slate-300 text-center">
          {t("aiDisclaimer")}
        </div>
      </div>
    </>
  );
};

export { ChatBlock };